import type {
  FormFieldInput, FormFieldUpdateInput, FormLayoutSection, FormSectionInput, FormSectionUpdateInput,
} from "@/lib/queries";
import type {
  AccessLevel, AccessRole, AdminForm, AdminFormField, AdminFormSection, FormAccessRule, FormEntity,
  FormFieldConfig, FormFieldOption, FormFieldType,
} from "@/lib/types";

/**
 * A tenant's custom fields as the console defines them (ACL-255, ACL-256, ADR-087 in `platform`): the
 * folders (the API's sections) a contact's or a firm's fields are grouped in, the fields, and what each
 * role may do with them, which an admin sets up on the Custom fields page.
 *
 * The rules are `platform`'s. The stored-name patterns, the eleven types' words and every limit below are
 * `services/shared/fields.py`, and which type change a filled field may make is core-api's. The server
 * decides all of it; the copies here keep a save it would refuse off the screen and say why first, as
 * the stages' and the dispositions' do. Pure, for `test/forms.test.ts`.
 */

// --- what the server takes -----------------------------------------------------------------------------

/** `KEY_PATTERN`: a section's or a field's stored name. Made from the title once and never changed. */
export const FORM_KEY_RE = /^[a-z][a-z0-9_]{0,39}$/;
/** `OPTION_KEY_PATTERN`: a choice's stored name. As a key, but it may start with a digit ("1099"). */
export const OPTION_KEY_RE = /^[a-z0-9][a-z0-9_]{0,39}$/;
export const KEY_MAX = 40;

export const FIELD_TITLE_MAX = 160;
export const SECTION_TITLE_MAX = 120;
export const OPTION_LABEL_MAX = 120;
/** The most options one dropdown or multi-select holds. */
export const OPTIONS_MAX = 200;
/** The longest a text may be made to allow, and a long text. */
export const TEXT_MAX_LENGTH = 1000;
export const LONG_TEXT_MAX_LENGTH = 10_000;
/** The most places a number may keep after the point. */
export const DECIMALS_MAX = 6;

/**
 * The stored names `platform` keeps for its own fields (`form_admin.RESERVED_KEYS` in core-api, which reads
 * the columns of a contact and of a firm from the models): ACL-259 adds those fields to the form under
 * these names, so a field made here never takes one. A title of "Email" is suggested `email_2`, and
 * typing `email` is refused before the save. A copy: the server decides, and a column added there later
 * is refused there first.
 */
export const RESERVED_FIELD_KEYS: readonly string[] = [
  // The columns of a contact, and of a firm.
  "id", "tenant_id", "campaign_id", "phone", "first_name", "last_name", "language", "state", "timezone",
  "source", "source_ref", "consent_wording", "consent_captured_at", "stage", "disposition",
  "active_call_until", "conversation_hold_until", "dnc", "imessage_capable", "attributes", "created_at",
  "updated_at", "code", "name", "label", "states", "languages", "status", "pause_reason", "settings",
  // Names the platform reads without a column, and the tickets' own words for them.
  "email", "full_name", "consent", "firm", "firm_id", "firm_tag", "tags", "campaign", "owner",
];

const KEY_RULE = "Lowercase letters, digits and _, starting with a letter; at most 40.";
const OPTION_KEY_RULE = "Stored name: lowercase letters, digits and _; at most 40.";

// --- the words -------------------------------------------------------------------------------------------

/** The eleven types, in the order the ticket lists them and the picker offers them. */
export const FIELD_TYPES: readonly FormFieldType[] = [
  "text", "long_text", "number", "email", "phone", "date", "datetime", "boolean", "dropdown",
  "multi_select", "reference",
];

const TYPE_TEXT: Record<FormFieldType, { label: string; description: string }> = {
  text: { label: "Text", description: "A short answer, up to 1,000 characters." },
  long_text: { label: "Long Text", description: "Notes and free-form answers, up to 10,000 characters." },
  number: { label: "Number", description: "A whole number or a decimal, with an optional range." },
  email: { label: "Email", description: "An email address." },
  phone: { label: "Phone", description: "A phone number." },
  date: { label: "Date", description: "A calendar date." },
  datetime: { label: "Date & Time", description: "A date and a time of day." },
  boolean: { label: "Yes / No", description: "One or the other." },
  dropdown: { label: "Dropdown", description: "One choice from a list you write." },
  multi_select: { label: "Multi-select", description: "Any number of choices from a list you write." },
  reference: { label: "Reference", description: "A link to another record: a contact or a firm." },
};

export function fieldTypeText(type: string): { label: string; description: string } {
  return TYPE_TEXT[type as FormFieldType] ?? { label: type, description: "Not a type this console knows." };
}

export function fieldTypeLabel(type: string): string {
  return fieldTypeText(type).label;
}

/** The types that keep a list of options. */
export function hasOptions(type: string): boolean {
  return type === "dropdown" || type === "multi_select";
}

/** What a form's records are called: the contact's form is about contacts. */
export function recordNoun(entity: FormEntity): string {
  return entity === "firm" ? "firm" : "contact";
}

/** A kind of record as a reference names it. */
export function entityLabel(entity: string): string {
  return entity === "firm" ? "Firm" : entity === "lead" ? "Contact" : entity;
}

// --- who may see and edit -------------------------------------------------------------------------------

/** The roles an admin sets access for, in the order the page lists them. Admins always edit. */
export const ACCESS_ROLES: readonly AccessRole[] = ["supervisor", "rep"];
export const ACCESS_LEVELS: readonly AccessLevel[] = ["edit", "read", "hidden"];

export function roleLabel(role: AccessRole): string {
  return role === "supervisor" ? "Supervisors" : "Reps";
}

const LEVEL_TEXT: Record<AccessLevel, { label: string; description: string }> = {
  edit: { label: "Can edit", description: "Sees the answer and can change it." },
  read: { label: "Read only", description: "Sees the answer, cannot change it." },
  hidden: { label: "Hidden", description: "Not shown at all: the answer is never sent to them." },
};

export function levelText(level: AccessLevel): { label: string; description: string } {
  return LEVEL_TEXT[level];
}

/** A field's access for one role as the form edits it: its own level, or `folder` to follow its folder.
 *  A folder has no `folder` choice: with no rule of its own, a role reads. */
export type AccessChoice = "folder" | AccessLevel;

/**
 * The levels a field's own "Who can see and edit" offers. One of the platform's own fields (the name, the
 * phone, the email) is never written through a form, so "Can edit" would act as read only; and it is
 * always shown on the contact card, in the list and in the search, so "Hidden" would hide it from none of
 * them. Neither is offered. A level it already has stays offered, so an admin can see it and change it.
 */
export function fieldLevels(platformField: boolean, current: AccessChoice): AccessLevel[] {
  return ACCESS_LEVELS.filter((level) => !platformField || level === "read" || level === current);
}

/** The level a role has with no rule anywhere: read only, as the ticket asks. */
export const DEFAULT_LEVEL: AccessLevel = "read";

/** One role's rule in a list, or undefined. */
export function ruleFor(rules: readonly FormAccessRule[] | null | undefined, role: AccessRole): AccessLevel | undefined {
  return rules?.find((r) => r.role === role)?.level;
}

/** A folder's level for a role: its rule, else read. */
export function folderLevel(section: { rules?: readonly FormAccessRule[] | null }, role: AccessRole): AccessLevel {
  return ruleFor(section.rules, role) ?? DEFAULT_LEVEL;
}

/** What a role ends up with on a field, as the server works it out (`rbac.field_access`): the field's own
 *  rule, else its folder's, else read. `inherited` says the field has no rule of its own for the role.
 *  One of the platform's own fields is never written through the form (ADR-087), so "edit" on it reads
 *  "read": its name, phone and the rest are changed where they always were (ACL-259). */
export function effectiveLevel(
  field: { rules?: readonly FormAccessRule[] | null; system?: boolean },
  section: { rules?: readonly FormAccessRule[] | null },
  role: AccessRole,
): { level: AccessLevel; inherited: boolean } {
  const own = ruleFor(field.rules, role);
  const got = own ? { level: own, inherited: false } : { level: folderLevel(section, role), inherited: true };
  return field.system && got.level === "edit" ? { ...got, level: "read" } : got;
}

/** The rules a set of choices sends: one per role that does not follow its folder. */
export function rulesFromChoices(choices: Readonly<Record<AccessRole, AccessChoice>>): FormAccessRule[] {
  return ACCESS_ROLES.flatMap((role) => {
    const level = choices[role];
    return level === "folder" ? [] : [{ role, level }];
  });
}

export function choicesFromRules(rules: readonly FormAccessRule[] | null | undefined): Record<AccessRole, AccessChoice> {
  return { supervisor: ruleFor(rules, "supervisor") ?? "folder", rep: ruleFor(rules, "rep") ?? "folder" };
}

/** A folder's levels as its form edits them: read where it has no rule. */
export function folderLevels(rules: readonly FormAccessRule[] | null | undefined): Record<AccessRole, AccessLevel> {
  return { supervisor: ruleFor(rules, "supervisor") ?? DEFAULT_LEVEL, rep: ruleFor(rules, "rep") ?? DEFAULT_LEVEL };
}

/** The rules a folder sends: only the roles that are not read only, since no rule already means read. */
export function folderRules(levels: Readonly<Record<AccessRole, AccessLevel>>): FormAccessRule[] {
  return ACCESS_ROLES.flatMap((role) => (levels[role] === DEFAULT_LEVEL ? [] : [{ role, level: levels[role] }]));
}

/** Two rule lists say the same, whatever their order. */
export function sameRules(a: readonly FormAccessRule[] | null | undefined, b: readonly FormAccessRule[] | null | undefined): boolean {
  return ACCESS_ROLES.every((role) => ruleFor(a, role) === ruleFor(b, role));
}

const grouped = (n: number) => n.toLocaleString("en-US");
const plural = (n: number, one: string, many = `${one}s`) => (n === 1 ? one : many);

// --- stored names ----------------------------------------------------------------------------------------

/** A title as a stored name: lowercase, every run of anything else one `_`, and cut to 40. */
function slugOf(text: string, leading: RegExp): string {
  return text
    .normalize("NFKD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "")
    .replace(leading, "")
    .slice(0, KEY_MAX)
    .replace(/_+$/, "");
}

/** `base`, or `base_2`, `base_3` ... for the first one not in `taken`; always within 40. */
function unique(base: string, taken: readonly string[]): string {
  if (!base) return base;
  const used = new Set(taken);
  if (!used.has(base)) return base;
  for (let n = 2; ; n += 1) {
    const suffix = `_${n}`;
    const candidate = `${base.slice(0, KEY_MAX - suffix.length).replace(/_+$/, "")}${suffix}`;
    if (!used.has(candidate)) return candidate;
  }
}

/**
 * A section's or a field's stored name, suggested from its title: "Accident Date" is `accident_date`,
 * and a second one is `accident_date_2`. It has to start with a letter, so leading digits go
 * ("2nd Call" is `nd_call`, as a stage's slug is), and an empty answer means the title has nothing a
 * stored name can be made of: the admin types one. `taken` is every key the form already holds,
 * archived ones too, since a key is never reused, and `reserved` are the names the platform keeps
 * (`RESERVED_FIELD_KEYS`, for a field).
 */
export function keyFromTitle(
  title: string,
  taken: readonly string[] = [],
  reserved: readonly string[] = [],
): string {
  return unique(slugOf(title, /^[0-9_]+/), [...taken, ...reserved]);
}

/** A choice's stored name, suggested from its label. As a key, but it may start with a digit, and
 *  `taken` is the field's other options. */
export function optionKeyFromLabel(label: string, taken: readonly string[] = []): string {
  return unique(slugOf(label, /^_+/), taken);
}

// --- numbers typed into a form ---------------------------------------------------------------------------

const NUMBER_RE = /^[+-]?(\d+(\.\d*)?|\.\d+)([eE][+-]?\d+)?$/;

/** A number as typed, or null for anything else. "" is not a number: callers check for empty first. */
function parseNumber(text: string): number | null {
  const t = text.trim();
  if (!NUMBER_RE.test(t)) return null;
  const n = Number(t);
  return Number.isFinite(n) ? n : null;
}

function parseWhole(text: string): number | null {
  const n = parseNumber(text);
  return n !== null && Number.isInteger(n) ? n : null;
}

const asText = (n: number | undefined) => (n === undefined ? "" : String(n));

// --- the field form --------------------------------------------------------------------------------------

/** One row of a dropdown's or a multi-select's options while it is being edited. */
export interface OptionDraft {
  /** Which row it is while editing: `saved:<key>` for an option that exists, `new:<n>` for one added here. */
  id: string;
  key: string;
  label: string;
  /** It exists already, so its stored name is fixed: contacts keep the name, not the label. */
  saved: boolean;
  /** The stored name has been typed in, so it stops following the label. */
  typed: boolean;
}

/** The field form. Numbers are text because that is what an input hands back; `configFromValues`
 *  reads the ones that belong to the type. */
export interface FieldValues {
  title: string;
  key: string;
  type: FormFieldType;
  /** The key of the section it is in. */
  section: string;
  required: boolean;
  /** Each role's access: its own level, or `folder` to follow the folder's. */
  access: Record<AccessRole, AccessChoice>;
  /** `text` and `long_text`; empty for the most the type allows. */
  max_length: string;
  /** `number`; each empty for none. */
  min: string;
  max: string;
  decimals: string;
  /** `dropdown` and `multi_select`. */
  options: OptionDraft[];
  /** `reference`: the kind of record it points at. */
  target: FormEntity | "";
  /** `reference`: the key of the target form's field that names the record, or "" for its own name. */
  display: string;
}

export const NEW_FIELD: FieldValues = {
  title: "",
  key: "",
  type: "text",
  section: "",
  required: false,
  access: { supervisor: "folder", rep: "folder" },
  max_length: "",
  min: "",
  max: "",
  decimals: "",
  options: [],
  target: "",
  display: "",
};

/** A new field's form, in the section "New field" was pressed in. */
export function newFieldValues(section: string): FieldValues {
  return { ...NEW_FIELD, section, options: [], access: { ...NEW_FIELD.access } };
}

/** An existing field's form. The design nests a field in its section, so the section is named here. */
export function fieldValues(field: AdminFormField, section: string): FieldValues {
  const config = field.config ?? {};
  return {
    title: field.title,
    key: field.key,
    type: field.type,
    section,
    required: field.required,
    access: choicesFromRules(field.rules),
    max_length: asText(config.max_length),
    min: asText(config.min),
    max: asText(config.max),
    decimals: asText(config.decimals),
    options: (config.options ?? []).map((o) => ({
      id: `saved:${o.key}`, key: o.key, label: o.label, saved: true, typed: true,
    })),
    target: config.target ?? "",
    display: config.display ?? "",
  };
}

/**
 * The settings the form describes, for its type and no other: a text's length, a number's range, a
 * choice's options, a reference's target. What was typed under another type stays in the form, so
 * changing type back finds it, and is never sent. Whatever was left empty is left out.
 */
export function configFromValues(values: FieldValues): FormFieldConfig {
  switch (values.type) {
    case "text":
    case "long_text": {
      const length = values.max_length.trim() ? parseWhole(values.max_length) : null;
      return length === null ? {} : { max_length: length };
    }
    case "number": {
      const out: FormFieldConfig = {};
      const min = values.min.trim() ? parseNumber(values.min) : null;
      const max = values.max.trim() ? parseNumber(values.max) : null;
      const decimals = values.decimals.trim() ? parseWhole(values.decimals) : null;
      if (min !== null) out.min = min;
      if (max !== null) out.max = max;
      if (decimals !== null) out.decimals = decimals;
      return out;
    }
    case "dropdown":
    case "multi_select":
      return { options: values.options.map((o) => ({ key: o.key, label: o.label.trim() })) };
    case "reference":
      if (!values.target) return {};
      return values.display ? { target: values.target, display: values.display } : { target: values.target };
    default:
      return {};
  }
}

/** The types a reference may be shown by: one line of text a person reads as a name. */
const DISPLAY_TYPES: readonly FormFieldType[] = ["text", "long_text", "email", "phone", "number", "date", "dropdown"];

/**
 * The fields of the target form a reference can be shown by ("Shown as"), besides the record's own name:
 * an active field of an admin's own that holds one line a person reads (the server's rule for `display`).
 */
export function displayChoices(target: Pick<AdminForm, "sections"> | undefined): { key: string; title: string }[] {
  return (target?.sections ?? [])
    .filter((s) => !s.archived)
    .flatMap((s) => s.fields)
    .filter((f) => !f.archived && f.storage === "value" && DISPLAY_TYPES.includes(f.type))
    .map((f) => ({ key: f.key, title: f.title }));
}

/** The options of a field or a config, in order. */
export function optionsOf(field: { config: FormFieldConfig }): FormFieldOption[] {
  return field.config?.options ?? [];
}

// --- editing a field's options ---------------------------------------------------------------------------

export function blankOption(id: string): OptionDraft {
  return { id, key: "", label: "", saved: false, typed: false };
}

/** The id for an option added to this list: `new:<n>`, one past the highest already there, so two rows
 *  never share one even after rows were removed. Pure, since a state update may run it twice. */
export function nextOptionId(options: readonly OptionDraft[]): string {
  let highest = -1;
  for (const o of options) {
    const n = /^new:(\d+)$/.exec(o.id);
    if (n) highest = Math.max(highest, Number(n[1]));
  }
  return `new:${highest + 1}`;
}

/** The options with row `index`'s label changed. A row that is new, and whose stored name nobody has
 *  typed in, takes its stored name from the label. */
export function withOptionLabel(options: readonly OptionDraft[], index: number, label: string): OptionDraft[] {
  return options.map((o, i) => {
    if (i !== index) return o;
    if (o.saved || o.typed) return { ...o, label };
    const others = options.filter((_, j) => j !== index).map((x) => x.key);
    return { ...o, label, key: optionKeyFromLabel(label, others) };
  });
}

/** The options with row `index`'s stored name typed in; it stops following the label. A saved
 *  option's stored name is fixed, so this leaves it alone. */
export function withOptionKey(options: readonly OptionDraft[], index: number, key: string): OptionDraft[] {
  return options.map((o, i) => (i === index && !o.saved ? { ...o, key, typed: true } : o));
}

export function withoutOption(options: readonly OptionDraft[], index: number): OptionDraft[] {
  return options.filter((_, i) => i !== index);
}

// --- what the field form refuses -------------------------------------------------------------------------

export interface FieldProblems {
  title?: string;
  key?: string;
  section?: string;
  required?: string;
  max_length?: string;
  min?: string;
  max?: string;
  decimals?: string;
  /** The list as a whole: empty, or too long. */
  options?: string;
  /** What is wrong with a row, by its position. */
  optionRows?: Record<number, string>;
  target?: string;
}

export function hasProblems(problems: object): boolean {
  return Object.keys(problems).length > 0;
}

/**
 * Why the field form cannot be saved, by control. The stored name is checked only on create, where
 * it is typed, and `taken` is every field key of the form, archived ones too. A platform field
 * (`system`) is saved with a title, a section and who sees it, so its settings, which cannot be
 * edited, are not held against it. A courtesy copy of `check_config`, `KEY_PATTERN` and the reference
 * rule: the server decides.
 */
export function fieldProblems(
  values: FieldValues,
  taken: readonly string[],
  creating: boolean,
  system = false,
): FieldProblems {
  const out: FieldProblems = {};
  const title = values.title.trim();
  if (!title) out.title = "Give the field a name.";
  else if (title.length > FIELD_TITLE_MAX) out.title = `At most ${FIELD_TITLE_MAX} characters.`;
  if (creating) {
    if (!FORM_KEY_RE.test(values.key)) out.key = KEY_RULE;
    else if (RESERVED_FIELD_KEYS.includes(values.key)) out.key = "Kept for the platform's own field of that name.";
    else if (taken.includes(values.key)) out.key = "Another field already has this key.";
  }
  if (!values.section) out.section = "Pick the folder it goes in.";
  if (system) return out;

  switch (values.type) {
    case "text":
    case "long_text": {
      const ceiling = values.type === "text" ? TEXT_MAX_LENGTH : LONG_TEXT_MAX_LENGTH;
      if (values.max_length.trim()) {
        const length = parseWhole(values.max_length);
        if (length === null || length < 1 || length > ceiling) {
          out.max_length = `A whole number from 1 to ${grouped(ceiling)}, or leave it empty.`;
        }
      }
      break;
    }
    case "number": {
      const min = values.min.trim() ? parseNumber(values.min) : undefined;
      const max = values.max.trim() ? parseNumber(values.max) : undefined;
      if (min === null) out.min = "Enter a number, or leave it empty.";
      if (max === null) out.max = "Enter a number, or leave it empty.";
      if (typeof min === "number" && typeof max === "number" && min > max) {
        out.max = "The largest cannot be below the smallest.";
      }
      if (values.decimals.trim()) {
        const places = parseWhole(values.decimals);
        if (places === null || places < 0 || places > DECIMALS_MAX) {
          out.decimals = `A whole number from 0 to ${DECIMALS_MAX}, or leave it empty.`;
        }
      }
      break;
    }
    case "dropdown":
    case "multi_select": {
      const rows = values.options;
      if (rows.length === 0) out.options = "Add at least one option.";
      else if (rows.length > OPTIONS_MAX) out.options = `At most ${OPTIONS_MAX} options.`;
      const problems: Record<number, string> = {};
      const labels = new Set<string>();
      const keys = new Set<string>();
      rows.forEach((row, i) => {
        const label = row.label.trim();
        const folded = label.toLowerCase();
        if (!label) problems[i] = "Give the option a name.";
        else if (label.length > OPTION_LABEL_MAX) problems[i] = `At most ${OPTION_LABEL_MAX} characters.`;
        else if (!OPTION_KEY_RE.test(row.key)) problems[i] = OPTION_KEY_RULE;
        else if (labels.has(folded)) problems[i] = "Another option already has this name.";
        else if (keys.has(row.key)) problems[i] = "Another option already has this key.";
        if (label) labels.add(folded);
        if (row.key) keys.add(row.key);
      });
      if (Object.keys(problems).length > 0) out.optionRows = problems;
      break;
    }
    case "reference":
      if (values.target !== "lead" && values.target !== "firm") out.target = "Pick what it points to.";
      break;
    default:
      break;
  }
  return out;
}

/**
 * The types a field that holds values may become, which are the ones that change nothing stored: a
 * text is already a string a long text holds, and an email or a phone is a string a text holds. A
 * courtesy copy of core-api's rule; the server decides.
 */
const FILLED_CHANGES: Partial<Record<FormFieldType, readonly FormFieldType[]>> = {
  text: ["long_text"],
  email: ["text", "long_text"],
  phone: ["text", "long_text"],
};

/** "1 contact holds" or "12 contacts hold": how many records keep a value in a field. */
function heldBy(count: number, noun: string): string {
  return count === 1 ? `1 ${noun} holds` : `${grouped(count)} ${noun}s hold`;
}

/** The line under the type of a field that records hold a value in, or null when none does. */
export function filledNote(count: number, noun = "contact"): string | null {
  return count > 0 ? `${heldBy(count, noun)} a value in it, so its type can only change between the text types.` : null;
}

/**
 * Why a field cannot be changed to `to`, in a sentence, or null when it can. A platform field never
 * changes type. A field nobody has a value in can become anything; a field that records hold a value in,
 * only a type that keeps every value as it is. Otherwise the server answers 409, and this says so before
 * the save and names how many records hold a value. `noun` is what the form's records are called.
 */
export function typeChangeProblem(
  field: Pick<AdminFormField, "type" | "values" | "system">,
  to: FormFieldType,
  noun = "contact",
): string | null {
  if (to === field.type) return null;
  if (field.system) return "One of the platform's own fields: its type cannot change.";
  // No count, or none, is no value held.
  if (!(field.values > 0)) return null;
  if (FILLED_CHANGES[field.type]?.includes(to)) return null;
  return (
    `${heldBy(field.values, noun)} a value in this field, so its type cannot change to ${fieldTypeLabel(to)}. ` +
    `A field that holds values can only move between the text types (Text to Long Text, Email or Phone to ` +
    `Text or Long Text); to use another type, archive it and add a new field.`
  );
}

/** What `POST /forms/{entity}/fields` takes. A field that follows its folder for every role sends no
 *  rules. */
export function newFieldPayload(values: FieldValues): FormFieldInput {
  const rules = rulesFromChoices(values.access);
  return {
    section: values.section,
    title: values.title.trim(),
    type: values.type,
    required: values.required,
    config: configFromValues(values),
    ...(rules.length ? { rules } : {}),
    key: values.key,
  };
}

/**
 * What an edit sends: only what changed, compared as the form reads them (`before` is the form as it
 * opened). Settings go when they change, and always with a new type, because the ones of the old type
 * would be refused under it. Access goes as the whole rule set when any role's choice changed: the server
 * replaces the field's rules with what is sent. A platform field (`system`) sends a title, a folder and
 * its access, and no more.
 */
export function fieldChanges(before: FieldValues, after: FieldValues, system = false): FormFieldUpdateInput {
  const out: FormFieldUpdateInput = {};
  const title = after.title.trim();
  if (title !== before.title.trim()) out.title = title;
  if (after.section !== before.section) out.section = after.section;
  if (ACCESS_ROLES.some((role) => after.access[role] !== before.access[role])) out.rules = rulesFromChoices(after.access);
  if (system) return out;
  if (after.type !== before.type) out.type = after.type;
  if (after.required !== before.required) out.required = after.required;
  const config = configFromValues(after);
  if (after.type !== before.type || JSON.stringify(config) !== JSON.stringify(configFromValues(before))) {
    out.config = config;
  }
  return out;
}

// --- the section form ------------------------------------------------------------------------------------

export interface SectionValues {
  title: string;
  key: string;
  /** Each role's level on the folder's fields that have no rule of their own. */
  access: Record<AccessRole, AccessLevel>;
}

export const NEW_SECTION: SectionValues = { title: "", key: "", access: { supervisor: "read", rep: "read" } };

export function sectionValues(section: AdminFormSection): SectionValues {
  return { title: section.title, key: section.key, access: folderLevels(section.rules) };
}

/** Why the section form cannot be saved. `taken` is every section key of the form, archived ones too. */
export function sectionProblems(
  values: SectionValues,
  taken: readonly string[],
  creating: boolean,
): Partial<Record<keyof SectionValues, string>> {
  const out: Partial<Record<keyof SectionValues, string>> = {};
  const title = values.title.trim();
  if (!title) out.title = "Give the folder a name.";
  else if (title.length > SECTION_TITLE_MAX) out.title = `At most ${SECTION_TITLE_MAX} characters.`;
  if (creating) {
    if (!FORM_KEY_RE.test(values.key)) out.key = KEY_RULE;
    else if (taken.includes(values.key)) out.key = "Another folder already has this key.";
  }
  return out;
}

export function newSectionPayload(values: SectionValues): FormSectionInput {
  const rules = folderRules(values.access);
  return { title: values.title.trim(), key: values.key, ...(rules.length ? { rules } : {}) };
}

/** What an edit sends: the name when it changed, and the whole rule set when any role's level did. */
export function sectionChanges(values: SectionValues, section: AdminFormSection): FormSectionUpdateInput {
  const out: FormSectionUpdateInput = {};
  const title = values.title.trim();
  if (title !== section.title) out.title = title;
  const rules = folderRules(values.access);
  if (!sameRules(rules, section.rules)) out.rules = rules;
  return out;
}

// --- archiving a section ---------------------------------------------------------------------------------

/** How many fields of the section are not archived. */
export function activeFieldCount(section: Pick<AdminFormSection, "fields">): number {
  return section.fields.filter((f) => !f.archived).length;
}

/** The fields' count as the section's header says it: "3 fields", and how many more are archived. */
export function fieldCountText(section: Pick<AdminFormSection, "fields">): string {
  const active = activeFieldCount(section);
  const archived = section.fields.length - active;
  const live = active === 0 ? "No fields" : `${active} ${plural(active, "field")}`;
  return archived > 0 ? `${live}, ${archived} archived` : live;
}

/**
 * Why a section cannot simply be archived, in the sentence the dialog opens with, or null when it
 * has no field in use. The server refuses to archive one that still holds active fields, unless it is
 * told what becomes of them: move them to another section, or archive them with it.
 */
export function archiveSectionBlocker(section: Pick<AdminFormSection, "title" | "fields">): string | null {
  const live = activeFieldCount(section);
  if (live === 0) return null;
  const held = `${section.title} still holds ${live} ${plural(live, "field")}.`;
  return live === 1
    ? `${held} Move the field to another folder, or archive it with the folder.`
    : `${held} Move the fields to another folder, or archive them with the folder.`;
}

/** What archiving a field does to the values it holds, for its confirm: nothing is lost. */
export function archiveFieldNote(count: number, noun = "contact"): string {
  const end = "Nothing is deleted: restoring the field brings it back.";
  if (count <= 0) return `No ${noun} holds a value in it. ${end}`;
  const who = count === 1 ? `The ${noun} that holds` : `The ${grouped(count)} ${noun}s that hold`;
  return `${who} a value in it ${count === 1 ? "keeps" : "keep"} it. ${end}`;
}

/** What happens to a section's fields when it is archived. */
export type ArchiveChoice = { mode: "move"; to: string } | { mode: "archive" };

/** What archiving sends: a section with no active field just archives, and any other says what
 *  becomes of its fields. */
export function archiveSectionPayload(
  section: Pick<AdminFormSection, "fields">,
  choice: ArchiveChoice,
): FormSectionUpdateInput {
  if (activeFieldCount(section) === 0) return { archived: true };
  return {
    archived: true,
    fields: choice.mode === "move" ? { action: "move", to: choice.to } : { action: "archive" },
  };
}

/** What archiving did to the section's fields, for the toast, or undefined when it had none. */
export function archivedSummary(
  section: Pick<AdminFormSection, "fields">,
  choice: ArchiveChoice,
  moveTitle: string | null,
): string | undefined {
  const live = activeFieldCount(section);
  if (live === 0) return undefined;
  const fields = `${live} ${plural(live, "field")}`;
  return choice.mode === "move" ? `${fields} moved to ${moveTitle ?? choice.to}.` : `${fields} archived with it.`;
}

// --- what the design holds -------------------------------------------------------------------------------

/** Every field key of the form, archived ones too: a key is never reused. */
export function fieldKeys(form: Pick<AdminForm, "sections"> | undefined): string[] {
  return (form?.sections ?? []).flatMap((s) => s.fields.map((f) => f.key));
}

export function sectionKeys(form: Pick<AdminForm, "sections"> | undefined): string[] {
  return (form?.sections ?? []).map((s) => s.key);
}

/** The sections a field can be put in: the active ones, and the one it is in already. */
export function sectionChoices(form: Pick<AdminForm, "sections"> | undefined, current?: string): AdminFormSection[] {
  return (form?.sections ?? []).filter((s) => !s.archived || s.key === current);
}

// --- ordering --------------------------------------------------------------------------------------------

/** The list with the item named `key` moved one place up or down, or null when it is already at that
 *  end or is not in it. */
export function moveWithin<T extends { key: string }>(list: readonly T[], key: string, by: -1 | 1): T[] | null {
  const at = list.findIndex((item) => item.key === key);
  const other = at + by;
  if (at < 0 || other < 0 || other >= list.length) return null;
  const next = [...list];
  [next[at], next[other]] = [next[other]!, next[at]!];
  return next;
}

/**
 * The layout the server takes (`PUT /forms/{entity}/layout`) from the design as it stands: every
 * section that is not archived, once, in order, each with its fields that are not archived, once, in
 * order. An archived section and an archived field are not in it: the server leaves them where they are.
 */
export function layoutFrom(sections: readonly AdminFormSection[]): FormLayoutSection[] {
  return sections
    .filter((s) => !s.archived)
    .map((s) => ({ key: s.key, fields: s.fields.filter((f) => !f.archived).map((f) => f.key) }));
}

/** The whole layout after moving a section one place up or down among the sections that are not
 *  archived, or null when it is already at that end. */
export function movedSectionLayout(
  sections: readonly AdminFormSection[],
  key: string,
  by: -1 | 1,
): FormLayoutSection[] | null {
  const moved = moveWithin(sections.filter((s) => !s.archived), key, by);
  return moved ? layoutFrom(moved) : null;
}

/** The whole layout after moving a field one place up or down within its section, among the fields
 *  that are not archived, or null when it is already at that end. */
export function movedFieldLayout(
  sections: readonly AdminFormSection[],
  sectionKey: string,
  fieldKey: string,
  by: -1 | 1,
): FormLayoutSection[] | null {
  const layout = layoutFrom(sections);
  const target = layout.find((s) => s.key === sectionKey);
  if (!target) return null;
  const moved = moveWithin(target.fields.map((key) => ({ key })), fieldKey, by);
  if (!moved) return null;
  target.fields = moved.map((f) => f.key);
  return layout;
}

// --- what the page and its preview say about a field ----------------------------------------------------

/** A field's settings in a few words, for the table and under a previewed control. Null for the
 *  types that have none, or a field that has not set any. */
export function fieldDetail(field: { type: string; config: FormFieldConfig }): string | null {
  const config = field.config ?? {};
  switch (field.type) {
    case "text":
    case "long_text":
      return config.max_length ? `Up to ${grouped(config.max_length)} characters` : null;
    case "number": {
      const parts: string[] = [];
      if (config.min !== undefined && config.max !== undefined) parts.push(`${config.min} to ${config.max}`);
      else if (config.min !== undefined) parts.push(`${config.min} or more`);
      else if (config.max !== undefined) parts.push(`${config.max} or less`);
      if (config.decimals !== undefined) {
        parts.push(
          config.decimals === 0 ? "whole numbers" : `${config.decimals} decimal ${plural(config.decimals, "place")}`,
        );
      }
      const text = parts.join(", ");
      return text ? text.charAt(0).toUpperCase() + text.slice(1) : null;
    }
    case "dropdown":
    case "multi_select": {
      const n = config.options?.length ?? 0;
      return n === 0 ? "No options" : `${n} ${plural(n, "option")}`;
    }
    case "reference":
      return config.target ? `Points to a ${recordNoun(config.target)}` : null;
    default:
      return null;
  }
}

/** The options' labels in a line, the first few and how many more. */
export function optionsSummary(options: readonly FormFieldOption[], shown = 6): string {
  if (options.length === 0) return "No options yet";
  const labels = options.slice(0, shown).map((o) => o.label).join(", ");
  const more = options.length - shown;
  return more > 0 ? `${labels} and ${more} more` : labels;
}

/** The line under a previewed control: its options, or what its settings allow. */
export function previewHint(field: { type: string; config: FormFieldConfig }): string | null {
  if (hasOptions(field.type)) return `Options: ${optionsSummary(optionsOf(field))}`;
  if (field.type === "reference") {
    const target = field.config?.target;
    return `${target ? `Points to a ${recordNoun(target)}, ` : ""}picked from a search.`;
  }
  return fieldDetail(field);
}

// --- searchable fields (ADR-101) -------------------------------------------------------------------

/** The types the search can look in: the ones a person types words into, or picks a labelled option of. */
export const SEARCHABLE_TYPES: readonly FormFieldType[] = [
  "text", "long_text", "email", "phone", "dropdown", "multi_select",
];
/** How many fields of one form may be searchable at once, as GoHighLevel allows. */
export const SEARCHABLE_MAX = 6;

/** The platform's own fields the search always looks in, which the dialog shows locked on and the search
 *  box's hint names first. A firm's name is its label or its name; its tags are its aliases. */
export function alwaysSearched(entity: FormEntity): string[] {
  return entity === "lead" ? ["Name", "Phone", "Email"] : ["Name", "Code", "Tags"];
}

/**
 * The hint over a search box (ADR-101), as GoHighLevel's: "Search by name, phone, email, License Plate or
 * Claim number." The platform's own fields first, lower-cased as words of the sentence, then the titles of
 * the fields an admin marked searchable that this person's search reads (`GET /forms/{entity}/searchable`),
 * as the admin wrote them and each once: a superadmin's may hold one per tenant.
 */
export function searchHint(entity: FormEntity, titles: readonly string[]): string {
  const seen = new Set<string>();
  const marked = titles
    .map((title) => title.trim())
    .filter((title) => {
      const key = title.toLowerCase();
      if (!title || seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  const words = [...alwaysSearched(entity).map((name) => name.toLowerCase()), ...marked];
  return `Search by ${words.slice(0, -1).join(", ")} or ${words.at(-1)}.`;
}

/** Why a field cannot be searchable, or null when it can. The server says the same. */
export function searchableReason(field: Pick<AdminFormField, "type" | "system" | "storage" | "archived">): string | null {
  if (field.archived) return "Archived";
  if (field.system || field.storage !== "value") return "One of the platform's own fields";
  if (!SEARCHABLE_TYPES.includes(field.type)) return `${fieldTypeLabel(field.type)} fields cannot be searched`;
  return null;
}

export interface SearchableChoice {
  field: AdminFormField;
  section: AdminFormSection;
  /** Null when the field may be ticked. */
  reason: string | null;
}

/** Every active custom field of the form in its order, each with why it cannot be searched, if it cannot.
 *  The platform's own fields are not listed: they are always searched, or never (state, language). */
export function searchableChoices(form: Pick<AdminForm, "sections"> | undefined): SearchableChoice[] {
  if (!form) return [];
  return form.sections
    .filter((s) => !s.archived)
    .flatMap((section) =>
      section.fields
        .filter((f) => !f.archived && !f.system && f.storage === "value")
        .map((field) => ({ field, section, reason: searchableReason(field) })),
    );
}

/** The keys of the fields the form searches today. */
export function searchableKeys(form: Pick<AdminForm, "sections"> | undefined): string[] {
  return searchableChoices(form).filter((c) => c.field.searchable).map((c) => c.field.key);
}

/** Put `to` where `from` was, as a row of the dialog changes its field: the rows keep their places. A
 *  key already in the list is not added twice. */
export function replaceSearchable(keys: readonly string[], from: string, to: string): string[] {
  if (from === to) return [...keys];
  return keys.filter((k) => k !== to).map((k) => (k === from ? to : k));
}

/** A field a row of Edit searchable fields may be set to, under its folder. */
export interface SearchableOption {
  key: string;
  title: string;
  type: FormFieldType;
  folder: string;
}

/** What a row's picker offers: every active custom field of a searchable type, in the form's order, less
 *  the ones the other rows hold (`taken`). A row's own field stays on offer, so it reads as chosen. */
export function searchableOptions(
  form: Pick<AdminForm, "sections"> | undefined,
  taken: readonly string[],
): SearchableOption[] {
  const others = new Set(taken);
  return searchableChoices(form)
    .filter((c) => !c.reason && !others.has(c.field.key))
    .map(({ field, section }) => ({ key: field.key, title: field.title, type: field.type, folder: section.title }));
}

/** Whether two lists of keys name the same fields: the server takes the list as a set. */
export function sameSearchable(a: readonly string[], b: readonly string[]): boolean {
  return a.length === b.length && [...a].sort().join("\n") === [...b].sort().join("\n");
}

/** The objects whose list was changed in the dialog and differs from what is saved, in a fixed order: what
 *  Save sends. An object whose form is not read yet has nothing to send. */
export function changedSearchable(
  drafts: Partial<Record<FormEntity, readonly string[]>>,
  forms: Partial<Record<FormEntity, Pick<AdminForm, "sections"> | undefined>>,
): FormEntity[] {
  return (["lead", "firm"] as const).filter((entity) => {
    const draft = drafts[entity];
    const form = forms[entity];
    return draft !== undefined && form !== undefined && !sameSearchable(draft, searchableKeys(form));
  });
}

/** "3 of 6 used". */
export function searchableCount(n: number): string {
  return `${n} of ${SEARCHABLE_MAX} used`;
}

/** Why this list cannot be saved, or undefined when it can: the server's checks, made before asking. */
export function searchableProblem(keys: readonly string[], form: Pick<AdminForm, "sections"> | undefined): string | undefined {
  if (keys.length > SEARCHABLE_MAX) return `At most ${SEARCHABLE_MAX} fields can be searchable.`;
  const choices = new Map(searchableChoices(form).map((c) => [c.field.key, c]));
  for (const key of keys) {
    const choice = choices.get(key);
    if (!choice) return `${key} is not a field of this form.`;
    if (choice.reason) return `${choice.field.title}: ${choice.reason.toLowerCase()}.`;
  }
  return undefined;
}

/** Tick or untick a key. A ticked key goes last; the server takes the list as a set, so the order does
 *  not matter. */
export function toggleSearchable(keys: readonly string[], key: string, on: boolean): string[] {
  const without = keys.filter((k) => k !== key);
  return on ? [...without, key] : without;
}

// --- fields a voicemail greeting may say (ADR-113 in `platform`) ------------------------------------

/** The types a greeting can say aloud (`fields.SPEAKABLE_TYPES`): the ones with a spoken form. */
export const SPEAKABLE_TYPES: readonly FormFieldType[] = [
  "text", "number", "phone", "date", "datetime", "dropdown",
];

/** Why a field cannot be said in a greeting, or null when it can. The server says the same. */
export function speakableReason(field: Pick<AdminFormField, "type" | "system" | "storage" | "archived">): string | null {
  if (field.archived) return "Archived";
  if (field.storage !== "value") return "One of the platform's own fields";
  if (!SPEAKABLE_TYPES.includes(field.type)) return `${fieldTypeLabel(field.type)} fields cannot be spoken`;
  return null;
}

export interface SpeakableChoice {
  field: AdminFormField;
  section: AdminFormSection;
  /** Null when the field may be ticked. */
  reason: string | null;
}

/** Every active field of the form that holds its values there, in the form's order, each with why a
 *  greeting cannot say it, if it cannot. The platform's own fields are not listed: the ones a greeting
 *  can say (a first name, the firm's name) are fields of its own, always there. */
export function speakableChoices(form: Pick<AdminForm, "sections"> | undefined): SpeakableChoice[] {
  if (!form) return [];
  return form.sections
    .filter((s) => !s.archived)
    .flatMap((section) =>
      section.fields
        .filter((f) => !f.archived && f.storage === "value")
        .map((field) => ({ field, section, reason: speakableReason(field) })),
    );
}

/** The keys of the fields a greeting may say today. */
export function speakableKeys(form: Pick<AdminForm, "sections"> | undefined): string[] {
  return speakableChoices(form).filter((c) => c.field.speakable).map((c) => c.field.key);
}

/** Why this list cannot be saved, or undefined when it can: the server's checks, made before asking. */
export function speakableProblem(keys: readonly string[], form: Pick<AdminForm, "sections"> | undefined): string | undefined {
  const choices = new Map(speakableChoices(form).map((c) => [c.field.key, c]));
  for (const key of keys) {
    const choice = choices.get(key);
    if (!choice) return `${key} is not a field of this form.`;
    if (choice.reason) return `${choice.field.title}: ${choice.reason.toLowerCase()}.`;
  }
  return undefined;
}

/** How a script writes one of the form's fields: `lead.fields.date_of_incident`. */
export function greetingFieldName(entity: FormEntity, key: string): string {
  return `${entity}.fields.${key}`;
}
